import React from 'react';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import MetaTags from '../components/SEO/MetaTags';
import {
  CollectiveIndividualPlaceholder,
  EmbodiedAIPlaceholder,
  EthicsAIPlaceholder,
  RoboticsPlaceholder,
  HealthcarePlaceholder,
  UIDesignPlaceholder
} from '../components/BlogPlaceholders';

const BlogPostCard = ({ post, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className="related-article-card"
  >
    <div className="related-article-image">
      {post.placeholderComponent}
    </div>
    <div className="related-article-content">
      <div className="related-article-categories">
        {post.categories.map((category, idx) => (
          <span key={idx} className="related-article-category">
            {category}
          </span>
        ))}
      </div>
      <h3 className="related-article-title">{post.title}</h3>
      <p className="related-article-excerpt line-clamp-3">{post.excerpt}</p>
      <Link 
        to={post.categories.includes("Ethics") ? `/blog/ethics/${post.slug}` : `/blog/${post.slug}`}
        className="related-article-link"
      >
        Read Article <FiArrowRight />
      </Link>
    </div>
  </motion.div>
);

const Blog = () => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const postsPerPage = 6; // Number of posts to show in grid per page (excluding featured)

  // Scroll to top on component mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Sample blog posts (in a real app, these would come from an API or CMS)
  const blogPosts = [
    {
      title: "The Future of AI Companions: Beyond Voice Assistants",
      slug: "future-ai-companions",
      excerpt: "Explore how AI companions like Ainous are evolving beyond voice assistants to become true partners in daily life, with embodied presence and emotional intelligence.",
      placeholderComponent: <CollectiveIndividualPlaceholder />,
      categories: ["AI", "Future Tech"]
    },
    {
      title: "How Embodied AI Changes Human-Computer Interaction",
      slug: "embodied-ai-hci",
      excerpt: "A deep dive into the psychology and design principles behind embodied AI and how physical presence transforms our relationship with technology.",
      placeholderComponent: <EmbodiedAIPlaceholder />,
      categories: ["Design", "Psychology"]
    },
    {
      title: "Ethical Considerations in Personal AI Development",
      slug: "ethical-ai-development",
      excerpt: "Examining the ethical frameworks we use when creating AI companions that become integrated into users' personal lives and homes.",
      placeholderComponent: <EthicsAIPlaceholder />,
      categories: ["Ethics", "AI"]
    },
    {
      title: "Robotics Meets AI: Creating Ainous' Physical Form",
      slug: "robotics-meets-ai",
      excerpt: "A behind-the-scenes look at the engineering challenges and breakthroughs in developing Ainous' hardware platform.",
      placeholderComponent: <RoboticsPlaceholder />,
      categories: ["Robotics", "Engineering"]
    },
    {
      title: "AI Assistants in Healthcare: New Frontiers",
      slug: "ai-healthcare-frontiers",
      excerpt: "Exploring how AI companions like Ainous can support healthcare initiatives, from medication reminders to mental health check-ins.",
      placeholderComponent: <HealthcarePlaceholder />,
      categories: ["Healthcare", "AI"]
    },
    {
      title: "Designing for Trust: The UI of AI Companionship",
      slug: "designing-for-trust",
      excerpt: "How our design team approaches the challenge of creating interfaces that foster trust and long-term engagement with AI companions.",
      placeholderComponent: <UIDesignPlaceholder />,
      categories: ["Design", "UX"]
    },
    {
      title: "The Evolution of AI and Human Mind: A New Chapter in Predictive Efficiency",
      slug: "ai-human-mind-evolution",
      excerpt: "Exploring how artificial intelligence is creating new 'algorithmic niches' that act as selective pressures on human cognition, potentially leading to a new era of human-AI co-evolution.",
      placeholderComponent: <CollectiveIndividualPlaceholder />,
      categories: ["AI", "Psychology", "Future Tech"]
    }
  ];

  const categories = [
    "All",
    "AI",
    "Robotics",
    "Design",
    "Ethics",
    "Healthcare",
    "Future Tech",
    "Psychology"
  ];

  // Calculate pagination
  const totalPosts = blogPosts.length;
  const totalPages = Math.ceil((totalPosts - 1) / postsPerPage); // -1 for featured post

  // Get current page posts (excluding featured post)
  const indexOfLastPost = currentPage * postsPerPage + 1; // +1 for featured post offset
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = blogPosts.slice(indexOfFirstPost, indexOfLastPost);

  return (
    <div className="min-h-screen">
      <MetaTags 
        title="Blog | xdimension"
        description="Explore the latest insights on AI companions, robotics, and the future of human-computer interaction from the xdimension team."
        url="https://xdimension.ai/blog"
      />
      
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8">
            <FiArrowLeft className="mr-2" />
            Back to Home
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12 text-center"
          >
            <h1 className="text-3xl md:text-4xl font-display font-bold mb-4">The <span className="text-gradient">xdimension</span> Blog</h1>
            <p className="text-xl text-neutral-dark/80 max-w-3xl mx-auto">
              Insights and updates from our team on AI companions, robotics, and the future of human-computer interaction.
            </p>
          </motion.div>
          
          {/* Categories */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-10 overflow-x-auto"
          >
            <div className="flex gap-3 pb-2">
              {categories.map((category, index) => (
                <button
                  key={index}
                  className={`whitespace-nowrap px-4 py-2 rounded-full transition-colors ${
                    category === "All" 
                      ? "bg-primary-600 text-white" 
                      : "bg-white/50 backdrop-blur-sm hover:bg-primary-50"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </motion.div>
          
          {/* Featured Post */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="featured-article-card"
          >
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="h-full featured-article-image">
                {blogPosts[0].placeholderComponent}
              </div>
              <div className="featured-article-content">
                <div className="article-categories">
                  {blogPosts[0].categories.map((category, idx) => (
                    <span key={idx} className="article-category">
                      {category}
                    </span>
                  ))}
                </div>
                <h2 className="featured-article-title">{blogPosts[0].title}</h2>
                <p className="featured-article-excerpt">{blogPosts[0].excerpt}</p>
                <Link 
                  to={`/blog/${blogPosts[0].slug}`} 
                  className="featured-article-button"
                >
                  Read Article <FiArrowRight className="ml-2" />
                </Link>
              </div>
            </div>
          </motion.div>
          
          {/* Blog Post Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {currentPosts.map((post, index) => (
              <BlogPostCard key={index} post={post} index={index} />
            ))}
          </div>
          
          {/* Pagination */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="flex justify-center items-center gap-2 mt-12"
          >
            {/* Previous button */}
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                currentPage === 1
                  ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                  : 'bg-white/50 backdrop-blur-sm hover:bg-primary-50 text-neutral-dark'
              }`}
            >
              ←
            </button>

            {/* Page numbers */}
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  currentPage === page
                    ? 'bg-primary-600 text-white'
                    : 'bg-white/50 backdrop-blur-sm hover:bg-primary-50'
                }`}
              >
                {page}
              </button>
            ))}

            {/* Next button */}
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                currentPage === totalPages
                  ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                  : 'bg-white/50 backdrop-blur-sm hover:bg-primary-50 text-neutral-dark'
              }`}
            >
              →
            </button>
          </motion.div>
          
          {/* Newsletter Signup */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="glass-card p-8 md:p-12 rounded-xl mt-16 text-center"
          >
            <h3 className="text-2xl font-display font-bold mb-4">Stay Updated</h3>
            <p className="text-neutral-dark/80 mb-6 max-w-2xl mx-auto">
              Subscribe to our newsletter for the latest insights on AI companions, robotics, and the future of technology.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
              <input 
                type="email" 
                placeholder="Your email address" 
                className="flex-grow p-3 rounded-xl bg-white/80 border border-neutral-light/20 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button className="btn-primary whitespace-nowrap">Subscribe</button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Blog;